import { useEffect, useState, useCallback, useRef } from "react";

// Hook to manage keyboard navigation for suggestion lists.
// params:
// - suggestions: array of suggestion objects (expected to have `description`)
// - onSelect: function to call when a suggestion is selected (receives description)
// - setInputValue: setter to update the input value immediately as user navigates
// - inputValue: current input value (optional, used to restore if highlight cleared)
export default function useSuggestionNavigation({
  suggestions = [],
  onSelect = () => { },
  setInputValue = () => { },
  inputValue = "",
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const prevHighlightedIndexRef = useRef(null);
  // Track whether highlighting is coming from keyboard navigation or mouse hover.
  // We should NOT populate the input on hover—only on click/select (handled elsewhere)
  // or keyboard navigation (ArrowUp/ArrowDown + Enter).
  const navModeRef = useRef(null); // 'keyboard' | 'mouse' | null
  // keep a ref of the original value so we can restore when navigation cleared
  const [preNavValue, setPreNavValue] = useState(inputValue);

  // When NOT navigating suggestions (no highlight), keep preNavValue synced to typed input.
  useEffect(() => {
    if (highlightedIndex == null) setPreNavValue(inputValue);
  }, [inputValue, highlightedIndex]);

  // when highlightedIndex changes, update the input value to the suggestion description.
  // IMPORTANT: do NOT "restore" preNavValue on every suggestions change while nothing
  // is highlighted, otherwise normal typing/backspace can get overwritten.
  useEffect(() => {
    if (highlightedIndex != null && suggestions[highlightedIndex]) {
      const desc = suggestions[highlightedIndex].description;
      // Only populate the field during keyboard navigation; never on hover.
      if (navModeRef.current === "keyboard") {
        if (typeof setInputValue === "function") setInputValue(desc);
      }
    } else {
      // Restore only when we *exit* keyboard navigation (was highlighted via keyboard, now cleared)
      if (
        prevHighlightedIndexRef.current != null &&
        navModeRef.current === "keyboard"
      ) {
        if (typeof setInputValue === "function") setInputValue(preNavValue);
      }
    }
    prevHighlightedIndexRef.current = highlightedIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex, suggestions]);

  // handle keyboard events
  const onKeyDown = useCallback(
    (e) => {
      if (!suggestions || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        navModeRef.current = "keyboard";
        setHighlightedIndex((cur) => {
          if (cur == null) return 0;
          return Math.min(suggestions.length - 1, cur + 1);
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        navModeRef.current = "keyboard";
        setHighlightedIndex((cur) => {
          if (cur == null) return suggestions.length - 1;
          return Math.max(0, cur - 1);
        });
      } else if (e.key === "Enter") {
        // if a suggestion is highlighted, select it
        if (highlightedIndex != null && suggestions[highlightedIndex]) {
          e.preventDefault();
          navModeRef.current = "keyboard";
          const desc = suggestions[highlightedIndex].description;
          try {
            onSelect(desc);
          } catch (err) {
            // swallow
          }
          // clear highlight after selecting
          setHighlightedIndex(null);
        }
      } else if (e.key === "Escape") {
        navModeRef.current = "keyboard";
        setHighlightedIndex(null);
      }
    },
    [suggestions, highlightedIndex, onSelect]
  );

  // called when mouse hover changes in suggestions list
  const onMouseHoverChange = useCallback((idx) => {
    navModeRef.current = "mouse";
    // keep keyboard state in sync when user mouses over suggestions
    setHighlightedIndex(idx);
  }, []);

  return {
    highlightedIndex,
    setHighlightedIndex,
    onKeyDown,
    onMouseHoverChange,
  };
}
